import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import Loader from "../../../components/misc/loader";
import AudienceCard from "../../aiAudienceExplorerV2/AudienceCard";
import DebugPanel from "./DebugPanel";
import { getAdminGenerationDetail } from "../../../services/agenticV2";
import { getCohortSizing } from "../../../services/cohort";
import { getTagBuilderConfiguration } from "../../../services/tag";
import { formatTargetingMode } from "./utils";
import "./index.scss";

function AdminAIGenerationDetail() {
	const { generationId } = useParams();
	const [detail, setDetail] = useState(null);
	const [loading, setLoading] = useState(true);
	const [collapsedCards, setCollapsedCards] = useState(new Set());
	const [audienceSizing, setAudienceSizing] = useState({});
	const [sizingLoading, setSizingLoading] = useState({});
	const [sizingFailed, setSizingFailed] = useState({});
	const [criteriaConfiguration, setCriteriaConfiguration] = useState([]);
	const [customUserDataConfiguration, setCustomUserDataConfiguration] = useState([]);
	const [modifierConfiguration, setModifierConfiguration] = useState([]);
	const [stackExpanded, setStackExpanded] = useState(false);

	useEffect(() => {
		document.title = "Generation Detail";
		fetchDetail();
	}, [generationId]);

	useEffect(() => {
		if (!detail?.silo_id) return;
		getTagBuilderConfiguration(detail.silo_id)
			.then((builderConfig) => {
				const publisherName = builderConfig?.publisherName ?? "";
				const config = builderConfig?.configuration?.siteContent ?? {};
				setCriteriaConfiguration(config.criteria ?? []);
				setModifierConfiguration((config.modifier ?? []).map((el) => ({
					...el,
					publisherName,
				})));
				const cudIntegrations = (builderConfig?.configuration?.customUserData?.integrations ?? []).map((el) => ({
					...el,
					publisherName,
				}));
				setCustomUserDataConfiguration(cudIntegrations);
			})
			.catch((err) => console.error("Failed to load builder configuration:", err));
	}, [detail?.silo_id]);

	const fetchDetail = async () => {
		setLoading(true);
		try {
			const result = await getAdminGenerationDetail(generationId);
			setDetail(result);
		} catch (error) {
			console.error("Error fetching generation detail:", error);
		} finally {
			setLoading(false);
		}
	};

	const toggleCardCollapse = (cardIndex) => {
		setCollapsedCards((prev) => {
			const next = new Set(prev);
			if (next.has(cardIndex)) {
				next.delete(cardIndex);
			} else {
				next.add(cardIndex);
			}
			return next;
		});
	};

	const count3PSegments = (audience) => {
		let count = 0;
		const cc = audience.composite_conditions;
		if (!cc) return 0;
		if (cc.criteriaGroups) {
			cc.criteriaGroups.forEach((group) => {
				group.forEach((criterion) => {
					const key = Object.keys(criterion)[0];
					if (key === "syndicated" && criterion.syndicated?.segments) {
						count += criterion.syndicated.segments.length;
					}
					if (key === "demographics") {
						const demo = criterion.demographics;
						if (demo) {
							Object.values(demo).forEach((val) => {
								if (Array.isArray(val)) count += val.length;
							});
						}
					}
				});
			});
		}
		return count;
	};

	const handleFetchSizing = async (audience, index) => {
		setSizingLoading((prev) => ({ ...prev, [index]: true }));
		try {
			const siloId = detail?.silo_id;
			if (!siloId) return;

			const payload = {
				data: [
					{
						name: audience.name || `Audience ${index + 1}`,
						description: audience.description || "",
						tagId: siloId,
						cohort_type: "composite",
						schema_version: "2.0",
						auto_generated: true,
						lookback: "30d",
						activation_methods: [],
						gamNetworkSelectionMode: "all",
						composite_conditions_v2: audience.composite_conditions,
					},
				],
			};

			const response = await getCohortSizing(payload);
			const data = await response.json();

			const failed = data.status === "timeout" || data.status === "failed";
			setSizingFailed((prev) => ({ ...prev, [index]: failed }));

			if (data.uniqueUsers !== undefined && data.pageViews !== undefined) {
				setAudienceSizing((prev) => ({
					...prev,
					[index]: {
						uniqueUsers: data.uniqueUsers || 0,
						pageViews: data.pageViews || 0,
					},
				}));
			}
		} catch (error) {
			console.error(`Error fetching sizing for audience ${index}:`, error);
			setSizingFailed((prev) => ({ ...prev, [index]: true }));
		} finally {
			setSizingLoading((prev) => ({ ...prev, [index]: false }));
		}
	};

	const formatDate = (dateStr) => {
		if (!dateStr) return "";
		const d = new Date(dateStr);
		return d.toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	if (loading) {
		return (
			<div className="wrapper admin-ai-generation-detail">
				<div className="loading-container">
					<Loader />
					<p>Loading generation detail...</p>
				</div>
			</div>
		);
	}

	if (!detail) {
		return (
			<div className="wrapper admin-ai-generation-detail">
				<div className="container">
					<Link to="/admin/ai-generations" className="back-link">
						&larr; Back to list
					</Link>
					<p>Generation not found.</p>
				</div>
			</div>
		);
	}

	const responseJson = detail.response_json || {};
	const audiences = responseJson.audiences || [];
	const debugInfo = responseJson.debug_info;
	const errorPayload = responseJson.error;

	// Group feedback by audience_index — a generation can hold feedback from
	// multiple users on the same audience (ASB-3074), so bucket into a list.
	const feedbackByIndex = {};
	if (detail.feedback) {
		detail.feedback.forEach((fb) => {
			if (!feedbackByIndex[fb.audience_index]) feedbackByIndex[fb.audience_index] = [];
			feedbackByIndex[fb.audience_index].push(fb);
		});
	}

	return (
		<div className="wrapper admin-ai-generation-detail">
			<Tooltip id="lookback-tooltip" />
			<div className="container">
				<Link to="/admin/ai-generations" className="back-link">
					&larr; Back to list
				</Link>

				<div className="detail-header">
					<h2>Generation Detail</h2>
					<div className="meta-row">
						<span className="meta-item">
							<strong>User:</strong> {detail.user_name} ({detail.user_email})
						</span>
						<span className="meta-item">
							<strong>Silo:</strong> {detail.silo_name}
						</span>
						<span className="meta-item">
							<strong>Generated:</strong> {formatDate(detail.created_at)}
						</span>
					</div>
				</div>

				{/* Prompt Details */}
				<div className="detail-card">
					<h3>Prompt Details</h3>
					<div className="detail-grid">
						{detail.targeting_text && (
							<div className="detail-field full-width">
								<label>Targeting Text</label>
								<p className="targeting-text-preview">
									{detail.targeting_text.length > 500
										? detail.targeting_text.substring(0, 500) + "..."
										: detail.targeting_text}
								</p>
							</div>
						)}
						{detail.file_name && (
							<div className="detail-field">
								<label>File</label>
								<p>{detail.file_name}{detail.file_format ? ` (${detail.file_format})` : ""}</p>
							</div>
						)}
						{detail.file_text && (
							<div className="detail-field full-width">
								<label>Extracted File Content</label>
								<p className="targeting-text-preview">
									{detail.file_text.length > 1000
										? detail.file_text.substring(0, 1000) + "..."
										: detail.file_text}
								</p>
							</div>
						)}
						<div className="detail-field">
							<label>Targeting Mode</label>
							<p>{formatTargetingMode(detail.targeting_mode)}</p>
						</div>
					</div>
				</div>

				{/* Error (failed run) */}
				{errorPayload && (
					<div className="detail-card error-card">
						<h3>Generation Failed</h3>
						<div className="detail-grid">
							{errorPayload.name && (
								<div className="detail-field">
									<label>Error</label>
									<p>{errorPayload.name}</p>
								</div>
							)}
							{errorPayload.timestamp && (
								<div className="detail-field">
									<label>Timestamp</label>
									<p>{formatDate(errorPayload.timestamp)}</p>
								</div>
							)}
							{errorPayload.message && (
								<div className="detail-field full-width">
									<label>Message</label>
									<pre className="error-message">{errorPayload.message}</pre>
								</div>
							)}
							{errorPayload.stack && (
								<div className="detail-field full-width">
									<button
										type="button"
										className="error-stack-toggle"
										onClick={() => setStackExpanded((v) => !v)}
									>
										{stackExpanded ? "Hide stack trace" : "Show stack trace"}
									</button>
									{stackExpanded && <pre className="error-stack">{errorPayload.stack}</pre>}
								</div>
							)}
						</div>
					</div>
				)}

				{/* Dropped audiences — planned themes that failed mid-pipeline but
				    didn't fail the whole run (ASB-3122). Surfaced here so the
				    results below are understood as a partial set. */}
				{Array.isArray(debugInfo?.dropped_audiences) && debugInfo.dropped_audiences.length > 0 && (
					<div className="detail-card dropped-card">
						<h3>
							Dropped Audiences
							<span className="dropped-count">{debugInfo.dropped_audiences.length}</span>
						</h3>
						<p className="dropped-intro">
							These planned audiences failed during generation and were dropped; the
							remaining results were unaffected.
						</p>
						<ul className="dropped-list">
							{debugInfo.dropped_audiences.map((d, i) => (
								<li key={i}>
									<span className="dropped-step">Step {d.step}</span>
									<span className="dropped-name">{d.name}</span>
									<span className="dropped-error">{d.error}</span>
								</li>
							))}
						</ul>
					</div>
				)}

				{/* Results */}
				{audiences.length > 0 && (
					<div className="detail-card results-card">
						<h3>Results</h3>

						{(responseJson.requirements_summary || responseJson.strategy_analysis) && (
							<div className="ai-section">
								<h4>Here is my understanding of the audience:</h4>
								<div className="ai-interpretation-box">
									<p className="ai-interpretation">
										{responseJson.requirements_summary || responseJson.strategy_analysis}
									</p>
								</div>
							</div>
						)}

						<div className="ai-section">
							<h4>I created this audience that matches the best:</h4>
							<div className="audiences-list">
								<AudienceCard
									audience={audiences[0]}
									cardIndex={0}
									sizingData={audienceSizing[0]}
									sizingIsLoading={sizingLoading[0]}
									sizingFailed={sizingFailed[0]}
									isCollapsed={collapsedCards.has(0)}
									onToggleCollapse={toggleCardCollapse}
									count3PSegments={count3PSegments}
									readOnly={true}
									existingFeedbackList={feedbackByIndex[0] || []}
									onFetchSizing={handleFetchSizing}
									generationId={detail.generation_id}
									showThemeBanner={false}
									criteriaConfiguration={criteriaConfiguration}
									customUserDataConfiguration={customUserDataConfiguration}
									modifierConfiguration={modifierConfiguration}
								/>
							</div>
						</div>

						{audiences.length > 1 && (
							<div className="ai-section">
								<h4>
									I explored {audiences.length - 1} variant
									{audiences.length - 1 !== 1 ? "s" : ""}:
								</h4>
								<div className="audiences-list">
									{audiences.slice(1).map((audience, index) => (
										<AudienceCard
											key={index + 1}
											audience={audience}
											cardIndex={index + 1}
											sizingData={audienceSizing[index + 1]}
											sizingIsLoading={sizingLoading[index + 1]}
											sizingFailed={sizingFailed[index + 1]}
											isCollapsed={collapsedCards.has(index + 1)}
											onToggleCollapse={toggleCardCollapse}
											count3PSegments={count3PSegments}
											readOnly={true}
											existingFeedbackList={feedbackByIndex[index + 1] || []}
											onFetchSizing={handleFetchSizing}
											generationId={detail.generation_id}
											criteriaConfiguration={criteriaConfiguration}
											customUserDataConfiguration={customUserDataConfiguration}
											modifierConfiguration={modifierConfiguration}
										/>
									))}
								</div>
							</div>
						)}
					</div>
				)}

				{/* Debug Information */}
				{debugInfo && (
					<div className="detail-card debug-section">
						<h3>Debug Information</h3>
						<DebugPanel debugInfo={debugInfo} audiences={audiences} />
					</div>
				)}
			</div>
		</div>
	);
}

export default AdminAIGenerationDetail;
